import { useCallback, useEffect, useMemo } from "react";

import { styled } from "styled-components";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { AlertBanner } from "@similarweb/ui-components";
import { Stack } from "@similarweb/ui-components/foundations";

import { DefaultErrorBoundary } from "app/components/error-boundary-no-data";
import { ActivationComponentAssets } from "app/pages/activation/helpers/ActivationAssets";
import type { SetDataToSave } from "app/pages/activation/modules/Settings/types";

import { generateAutomationName } from "../../../../helpers/generateAutomationName";

import { ContentFiltersHeader } from "./components/ContentFiltersHeader";
import { FilterCardList } from "./components/FilterCardList";
import type { SecondaryCompetitorsSectionProps } from "./components/SecondaryCompetitorsSection";
import { SecondaryCompetitorsSection } from "./components/SecondaryCompetitorsSection";
import { DATA_AUTOMATION_NAMES } from "./constants";
import { useContentFilterDrafts } from "./hooks/useContentFilterDrafts";
import { useDomainOptions } from "./hooks/useDomainOptions";
import { useSecondaryDomainsState } from "./hooks/useSecondaryDomainsState";

const Divider = styled.div`
    height: 1px;
    background-color: ${colorsPalettes.carbon[100]};
`;

type NewsSettingsTabProps = Pick<SecondaryCompetitorsSectionProps, "primaryDomain"> & {
    activationId: string;
    activationDomains: string[];
    setIsChanged: (isChanged: boolean) => void;
    setDataToSave: SetDataToSave;
};

const NewsSettingsTabContainer = ({
    activationId,
    activationDomains,
    primaryDomain,
    setIsChanged,
    setDataToSave,
}: NewsSettingsTabProps) => {
    const hasSecondaryListLimit = !Number.isNaN(ActivationComponentAssets.alertsSecondaryListLimit);
    const isSecondaryDomainsListEnabled = ActivationComponentAssets.alertsSecondaryListEnabled && hasSecondaryListLimit;
    const { translate } = useTranslate();

    const {
        handleSave: saveDomains,
        plainSecondaryDomains,
        secondaryDomains,
        isError: isDomainsError,
        ...secondaryDomainsProps
    } = useSecondaryDomainsState({
        activationId,
        setIsChanged,
    });

    const allDomains = useMemo(
        () => Array.from(new Set([...activationDomains, ...plainSecondaryDomains])).toSorted(),
        [activationDomains, plainSecondaryDomains],
    );

    const domainOptions = useDomainOptions({ activationDomains, secondaryDomains });

    const {
        handleSave: saveContentFilters,
        handleValidateFilters,
        isError: isContentFiltersError,
        ...contentFiltersProps
    } = useContentFilterDrafts({
        activationId,
        allDomains,
        setIsChanged,
    });

    const handleSaveNewsSettings = useCallback(async () => {
        await handleValidateFilters();

        if (isSecondaryDomainsListEnabled) {
            await saveDomains();
        }

        await saveContentFilters();
    }, [handleValidateFilters, isSecondaryDomainsListEnabled, saveDomains, saveContentFilters]);

    useEffect(() => {
        setDataToSave((current) => ({ ...current, onSaveNewsSettings: handleSaveNewsSettings }));
    }, [handleSaveNewsSettings, setDataToSave]);

    if (isDomainsError || isContentFiltersError) {
        return (
            <AlertBanner
                variant="error"
                data-automation={generateAutomationName("settings", [...DATA_AUTOMATION_NAMES, "error-banner"])}
            >
                <AlertBanner.Image />
                <AlertBanner.Description>{translate("common.error")}</AlertBanner.Description>
            </AlertBanner>
        );
    }

    return (
        <>
            <FilterCardList
                allDomains={allDomains}
                domainOptions={domainOptions}
                {...contentFiltersProps}
            />

            {isSecondaryDomainsListEnabled ? (
                <>
                    <Divider />
                    <SecondaryCompetitorsSection
                        {...secondaryDomainsProps}
                        secondaryDomains={secondaryDomains}
                        activationDomains={activationDomains}
                        primaryDomain={primaryDomain}
                    />
                </>
            ) : null}
        </>
    );
};

export const NewsSettingsTab = (props: NewsSettingsTabProps) => (
    <Stack
        width="100%"
        paddingY="24px"
        gap="24px"
        data-automation={generateAutomationName("settings", DATA_AUTOMATION_NAMES)}
    >
        <ContentFiltersHeader />
        <DefaultErrorBoundary>
            <NewsSettingsTabContainer {...props} />
        </DefaultErrorBoundary>
    </Stack>
);
